// Eager slim slice of the meet function barrel (bundle-fold, 2026-08-01).
//
// The full `./index.js` barrel re-exports the whole meet function surface —
// call connection, room CRUD, transcript analysis, suggestion handling,
// LiveKit plumbing, realtime subscriptions. sharedLibraries.js used to drag
// ALL of it into the shell's EAGER chunk by folding the full meet context.
//
// Meet is now a lazy sub-app (see components/eagerSlice.js): the `/meet` +
// `/meet-guest` routes fold the FULL meet context (components + functions +
// state) into the live shell context on first mount. So the eager chunk only
// needs the meet functions that get `el.call`'d (or statically imported) from
// OUTSIDE a meet-mounted component — i.e. on a surface that never folds the
// meet context.
//
// An exhaustive grep of the whole eager surface (workspace app.js/pages/
// components/functions, shared/**, chat, calendar, canvas, tickets, and the
// other lazy sub-apps' eager contexts) for cross-route meet-function callers
// found:
//   • connectCall — el.call'd by workspace pageContext.js (deep-link/notif
//     join), PersonPopover.js ("Meet" action), chat Composer.js/Main.js
//     (call button + rejoin), calendar EventDetailModal.js (join-call) — ALL
//     non-meet routes. Also the source of MeetCallWidget's own Return button
//     (static import of getLivekitRoom, not el.call — that whole module is
//     pulled in transitively regardless).
//   • disconnectCall — el.call'd by chat Main.js (leave from the chat call
//     bar) and MeetCallWidget's own Leave button.
//   • openNewRoomModal / closeNewRoomModal — el.call'd by the GLOBAL app
//     header "New meeting" action (shared/components/AppHeader.js,
//     shared/components/MainNavbar.js) and workspace/app.js itself.
//   • meetSetNewRoomVisibility — the `select` of that modal's Who can join
//     segments (AppSegmented el.call's it), which render in the same GLOBAL
//     layer before /meet folds the meet context.
//   • createRoom — el.call'd by PersonPopover.js, chat Composer.js/Main.js
//     ("start a call" from a DM/channel), calendar NewBookingModal.js/
//     EventDetailModal.js (schedule-with-a-call), AND MeetNewRoomModal's own
//     submit handler.
//   • meetRoomUrl — el.call'd by PersonPopover.js + calendar
//     NewBookingModal.js (share/copy a room link before ever opening /meet).
//   • markSuggestionApplied — el.call'd by calendar NewBookingModal.js +
//     tickets TicketDetailModal.js (save-a-prefilled-suggestion through the
//     native modal, without ever mounting a meet surface).
// Every OTHER meet function (subscribeMeetRealtime, muteParticipant,
// analyzeTranscript, applySuggestion, updateRoomSettings, admitGuest, …) is
// only el.call'd from a meet-mounted component, i.e. AFTER a fold registers
// it — so it rides the lazy chunk.
//   • subscribeMeetRealtime — el.call'd by the SHELL ROOT itself
//     (workspace/app.js, right after the notif poll wiring): shell-level meet
//     realtime so the "Start a call" icon can badge an ONGOING call before
//     /meet is ever opened. Missed by the original fold inventory — first
//     live boot logged "subscribeMeetRealtime is not registered" ×5 and the
//     ongoing-call badge died on non-meet routes. Light deps only (sdk getter
//     + pointer + row mappers; livekit-client stays a dynamic import inside
//     connectCall).
export { connectCall, disconnectCall } from './connectCall.js'
//   • meetCallAudioAttach / meetCallAudioMembers — the shell-root call-audio
//     host (components/CallAudio.js, mounted beside MeetCallWidget) plays a
//     live call on every route, long before /meet folds the meet context.
export { meetCallAudioAttach, meetCallAudioMembers } from './callAudio.js'
export { subscribeMeetRealtime } from './subscribeMeetRealtime.js'
export { openNewRoomModal, closeNewRoomModal, meetSetNewRoomVisibility } from './newRoomModal.js'
export { createRoom } from './createRoom.js'
export { meetRoomUrl } from './roomUrl.js'
//   • endRoom — el.call'd by the workspace CALENDAR BRIDGE
//     (workspace/functions/moduleCalendarBridge.js `moduleCalendarRemove`):
//     a module that retires a chained event must end the native room keyed by
//     that event, from whatever route the module is mounted on — never /meet.
//     Missed by the original inventory: `el.call` on an unregistered name is a
//     SILENT no-op, so the room stayed live and the bridge could not even see
//     the miss (WS-MODULE-CALENDAR-BRIDGE-UPDATE-REMOVE-1, caught live on the
//     bellforge workspace). Light deps only (the SDK getter + a root merge),
//     exactly like createRoom.
export { endRoom } from './endRoom.js'
//   • addMeetMember — el.call'd by the workspace's callPerson
//     (workspace/functions/callPerson.js: Fly's People and "call <name>", the
//     person popover's Call) from ANY route: the person called is made a
//     member of the private room before anyone joins, so they can see it and
//     their shell rings. Unregistered it would be the silent no-op endRoom's
//     note above describes. Light deps only (the SDK getter), like createRoom.
//     …and listMeetMembers: the person called is told apart from everyone else
//     who can SEE a private room (every workspace member can —
//     MeetService.assertCanAccessRoom) by the members list
//     (workspace/functions/incomingCall.js), on any route.
export { addMeetMember, listMeetMembers } from './manageMembers.js'
//   • meetLiveRecap* + analyzeTranscript — THE CALL'S SUMMARY BUILDS DURING
//     THE CALL (functions/meetLiveRecap.js, Nika 2026-10-23): the CALL's
//     realtime subscription (connectCall → subscribeMeetCallRealtime, eager)
//     counts each transcript line through `meetLiveRecap` and asks the
//     analysis again through `analyzeTranscript` — on whatever route the call
//     is on, before or without a /meet fold. Light deps only (the SDK getter).
export {
  meetLiveRecap,
  meetLiveRecapAsk,
  meetLiveRecapAsker,
  meetLiveRecapForce,
  meetLiveRecapOf
} from './meetLiveRecap.js'
//   • meetCallChanges* — WHAT CHANGED DURING THE CALL (functions/
//     meetCallChanges.js): the call's live summary reads the workspace's
//     timeline while the call runs, on whatever route it is on. Light deps
//     only (the SDK getter).
export {
  meetCallSince,
  meetCallChangesLoad,
  meetCallChangesGroup,
  meetCallChangesTick,
  meetCallChangesOf
} from './meetCallChanges.js'
export { analyzeTranscript } from './analyzeTranscript.js'
export { markSuggestionApplied } from './markSuggestionApplied.js'
//   • meetAiViews — the AI-answer views' PROVIDER (functions/meetAnswer.js):
//     the shell reads it on ANY route to know the calls views exist, before
//     the meet context folds; the views themselves fold it first
//     (`fold: 'meet'`, workspace functions/assistantAppViews.js).
export { meetAiViews } from './meetAnswer.js'
//   • meetFlyConfirmOf — el.call'd by the shell-root confirm card of a spoken
//     Fly ask (components/MeetFlyConfirm.js) on every route. A pure read of
//     root.meetFly; meetFlyState.js is already eager through the dock.
export { meetFlyConfirmOf } from './meetFlyState.js'
